<template>
    <div
        :class="showFilterOptions ? '' : 'h-18 overflow-hidden lg:h-auto'"
        class="filter-options p-4 space-y-4 mb-4 sm:mb-0 shadow-md text-sm transform duration-300 lg:block xl:p-8 bg-white rounded-lg sm:rounded-r-lg"
    >
        <div
            class="flex uppercase font-bold"
            @click="showFilterOptions = !showFilterOptions"
        >
            <span class="hidden sm:flex sm:grow text-secondary text-xl">Suchen &amp; Filtern</span>
            <UButton
                block
                class="sm:hidden"
                color="neutral"
                label="Suchen &amp; Filtern"
                variant="outline"
            />
        </div>

        <form @submit.prevent="search">
            <UInput
                v-model="filters.query"
                placeholder="Geben Sie ein Schlagwort ein"
                icon="i-lucide-search"
                size="lg"
                class="w-full shadow-md"
                type="search"
            />
        </form>

        <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
            Aktuelle Kategorie
        </div>
        <select
            v-model="categoryId"
            class="w-full px-4 py-2 border border-neutral-200 rounded shadow-md cursor-pointer"
            @update:model-value="search()"
        >
            <option
                v-for="item in items"
                :key="item.value"
                :value="item.value"
            >
                {{ item.label }}
            </option>
        </select>

        <template v-if="filteredCategory?.children?.length > 0 && filteredCategory?.hasArticles">
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
                Unterkategorien
            </div>

            <div class="py-2 space-y-2">
                <div
                    v-for="subCategory in filteredCategory?.children"
                    :key="subCategory._id"
                >
                    <div class="flex gap-1 items-center">
                        <UCheckbox
                            v-model="subCategory.selected"
                            :label="subCategory.name"
                            @change="search()"
                        />
                    </div>

                    <div
                        v-if="subCategory.children?.length"
                        class="py-2 space-y-2"
                    >
                        <div
                            v-for="(subSubCategory, subIndex) in subCategory.children"
                            :key="subIndex"
                            class="ml-8 flex gap-1 items-start"
                        >
                            <UCheckbox
                                v-model="subSubCategory.selected"
                                :label="subSubCategory.name"
                                @change="search()"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="py-2 space-y-2">
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
                Altersgruppe
            </div>
            <div class="flex gap-1 items-center">
                <UCheckbox
                    v-model="filters.under3"
                    @change="search()"
                />
                <label
                    class="cursor-pointer"
                    for="checkboxUnder3"
                >
                    Unter 3 Jahre
                </label>
            </div>
            <div class="flex gap-1 items-center">
                <UCheckbox
                    v-model="filters.over3"
                    @change="search()"
                />
                <label
                    class="cursor-pointer"
                    for="checkBoxOver3"
                >
                    Über 3 Jahre
                </label>
            </div>
        </div>

        <div class="py-2 space-y-2">
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
                Freie Fallhöhe bis
            </div>
            <USlider
                v-model="filters.maxFreeFallHeight"
                :min="0"
                :max="3"
                :step="0.5"
                :default-value="3"
                :tooltip="true"
                class="cursor-pointer py-2"
                @change="search()"
            />
            <div class="flex justify-between">
                <span>0</span>
                <span>0.5</span>
                <span>1</span>
                <span>1.5</span>
                <span>2</span>
                <span>2.5</span>
                <span>3</span>
            </div>
        </div>

        <div class="py-2 space-y-2">
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
                Verfügbare Fläche in Metern
            </div>
            <div class="flex gap-4 items-center">
                <UInput
                    v-model="filters.safetyAreaWidth"
                    type="number"
                    color="neutral"
                    variant="subtle"
                    class="grow w-20"
                    @change="search()"
                />
                <span class="shrink">x</span>
                <UInput
                    v-model="filters.safetyAreaDepth"
                    type="number"
                    color="neutral"
                    variant="subtle"
                    class="grow w-20 justify-end"
                    @change="search()"
                />
            </div>
        </div>

        <div class="py-2 space-y-2">
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200">
                Preis
            </div>
            <div class="flex gap-4">
                <UInput
                    v-model="filters.priceFrom"
                    placeholder="von"
                    type="number"
                    color="neutral"
                    variant="subtle"
                    class="grow w-20"
                    @change="search()"
                />
                <span class="shrink">-</span>
                <UInput
                    v-model="filters.priceTo"
                    placeholder="bis"
                    type="number"
                    color="neutral"
                    variant="subtle"
                    class="grow w-20 justify-end"
                    @change="search()"
                />
            </div>
        </div>

        <div
            id="inklusion"
            class="py-2 space-y-2"
        >
            <div class="uppercase text-sm font-bold border-b-2 border-neutral-200 flex gap-2 pb-2 items-center">
                Inklusion
                <UBadge
                    color="primary"
                    variant="solid"
                    class="animate-pulse"
                >
                    Neu
                </UBadge>
            </div>
            <div
                v-for="(group, index) in inclusionIcons"
                :key="index"
                class="flex gap-4"
            >
                <Accordion
                    :title="group.name"
                    accordion-class="w-full"
                    header-class="w-full flex gap-1 items-center justify-between cursor-pointer uppercase text-xs font-bold"
                >
                    <div
                        v-for="(icon, iconIndex) in group.icons"
                        :key="iconIndex"
                        class="flex gap-1 my-1 items-center"
                        @click="iconClicked(icon)"
                    >
                        <NuxtPicture
                            :src="`/images/icons/inclusion/${icon.filename}`"
                            :alt="icon.name"
                            class="w-10 h-10"
                            :class="{ 'selected-icon': isIconSelected(icon) }"
                        />
                        <span
                            class="cursor-pointer grow ml-2"
                            :class="{ 'text-secondary': isIconSelected(icon) }"
                        >
                            {{ icon.name }}
                      </span>
                    </div>
                </Accordion>
            </div>
        </div>

        <div class="py-2 space-y-2">
            <UButton
                block
                color="secondary"
                label="Filter zurücksetzen"
                variant="outline"
                @click="reset()"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import Accordion from '~/components/Accordion.vue';
import {StringTools} from '~~/utils/StringTools';
import {useMainCategories} from '~/composables/useMainCategories';
import {useFilterStore} from '~~/stores/filter';
import cleanGraphQLResponse from '~~/utils/clean-graphql';

const gql = useGql();
const props = defineProps<{
    predefinedCategory?: any
}>();

const emit = defineEmits<{(e: 'searching'): void
    (e: 'filter', articles: any[], meta?: { lastPage?: number }): void
}>();

const ALL = '0';
const router = useRouter();
const route = useRoute();
const {filters} = useFilterStore();

const categoryId = ref<string>(
    route.name === 'category' && route.params.id
        ? String(route.params.id)
        : ALL,
);

const predefinedCategoryId = ref(route.params.id);

const categories = ref(await useMainCategories());
categories.value.forEach((category) => {
    category.children?.forEach((child: any) => {
        child.selected = false;
        child.children?.forEach((subChild: any) => {
            subChild.selected = false;
        });
    });
});
const {data: categoryData} = await useAsyncGql({
    operation: 'findCategory',
    variables: {id: '/api/categories/' + String(categoryId.value)},
});

const category = computed(() => categoryData.value?.category ?? null);

const showFilterOptions = ref(false);

const items = computed(() => [
    {
        label: 'Alle Kategorien',
        value: ALL,
    },
    ...categories.value.map((c) => ({
        label: c.name,
        value: String(c._id),
    })),
]);

/**
 * Inklusion Icons
 */
function groupAndSortInclusionIcons(inclusionIcons: any) {
    const groups: any = {};

    // Group icons by group name
    inclusionIcons.forEach((icon: any) => {
        const groupName = icon.inclusionGroup.name;
        if (!groups[groupName]) {
            groups[groupName] = [];
        }
        groups[groupName].push(icon);
    });

    // Sort icons in each group by position
    for (const groupName in groups) {
        groups[groupName].sort((a: any, b: any) => a.position - b.position);
    }

    // Convert groups object to array and sort by position of first icon in each group
    return Object.keys(groups)
        .map((groupName) => ({
            name: groupName,
            icons: groups[groupName],
        }))
        .sort((a: any, b: any) => a.icons[0].position - b.icons[0].position);
}

const {data: inclusionIconData} = await useAsyncGql({operation: 'findInclusionIcons'});
const inclusionIcons = groupAndSortInclusionIcons(inclusionIconData.value?.inclusionIcons?.collection ?? []);

function isIconSelected(icon: any) {
    return filters.selectedInclusionIcons?.includes(icon._id);
}

function iconClicked(icon: any) {
    if (!filters.selectedInclusionIcons) {
        filters.selectedInclusionIcons = [];
    }

    if (filters.selectedInclusionIcons.includes(icon._id)) {
        filters.selectedInclusionIcons = filters.selectedInclusionIcons.filter((id: any) => id !== icon._id);
    } else {
        filters.selectedInclusionIcons.push(icon._id);
    }

    search();
}

/**
 * Filterreset
 */
function reset() {
    filters.query = '';
    filters.under3 = false;
    filters.over3 = false;
    filters.maxFreeFallHeight = 3;
    filters.safetyAreaWidth = null;
    filters.safetyAreaDepth = null;
    filters.priceFrom = null;
    filters.priceTo = null;
    filters.order = null;
    filters.selectedInclusionIcons = [];

    category.value?.children?.forEach((child: any) => {
        child.selected = false;
        child.children?.forEach((grandChild: any) => {
            grandChild.selected = false;
        });
    });

    performSearch();
}

/**
 * Suche
 */
async function performSearch(variables: any = {}) {
    emit('searching');

    variables.query = filters.query;

    filters.currentPage = (route.query?.page) ? parseInt(route.query?.page, 10) : 1;

    if (categoryId.value && categoryId.value !== ALL) {
        const subCategories: string[] = [];
        const mainCategory = categories.value.find((c) => String(c._id) === String(categoryId.value));
        mainCategory.children?.forEach((child: any) => {
            if (child.selected) {
                subCategories.push(String(child._id));
            }
            child.children?.forEach((subChild: any) => {
                if (subChild.selected) {
                    subCategories.push(String(subChild._id));
                }
            });
        });
        if (subCategories.length === 0) {
            subCategories.push(String(categoryId.value));
        }
        variables.categoriesList = subCategories;
    }

    // Altersgruppe
    if (filters.under3) {
        if (filters.over3) {
            variables.ageGroupFrom = [{lte: '3'}];
        } else {
            variables.ageGroupTo = [{lte: '3'}];
        }
    } else if (filters.over3) {
        variables.ageGroupFrom = [{gte: '3'}];
    }

    // Fallhöhe
    if (Number(filters.maxFreeFallHeight) < 3) {
        variables.freeFallHeight = [{lte: String(filters.maxFreeFallHeight)}];
    }

    // Sicherheitsbereich
    if (filters.safetyAreaWidth) {
        variables.safetyAreaWidth = [{lte: String(filters.safetyAreaWidth)}];
    }
    if (filters.safetyAreaDepth) {
        variables.safetyAreaDepth = [{lte: String(filters.safetyAreaDepth)}];
    }

    // Preisfilter
    if (filters.priceFrom) {
        variables.totalPrice = [{gte: String(filters.priceFrom)}];
    }
    if (filters.priceTo) {
        variables.totalPrice = variables.totalPrice ?? [];
        variables.totalPrice.push({lte: String(filters.priceTo)});
    }

    // Inclusion Icons
    if (Array.isArray(filters.selectedInclusionIcons) && filters.selectedInclusionIcons.length > 0) {
        variables.inclusionIcons = filters.selectedInclusionIcons;
    }

    // Sortierung
    const order = (route.query.order as 'asc' | 'desc' | undefined);
    if (order === 'asc' || order === 'desc') {
        variables.order = [{totalPrice: order}];
    } else {
        variables.order = [{priority: 'desc'}];
    }

    // Paging
    if (variables.page === undefined) {
        variables.page = (filters.currentPage == null) ? 1 : filters.currentPage;
    }
    const articleData = await gql('findArticles', variables);
    const resultArticles = cleanGraphQLResponse(articleData)?.articles;
    filters.lastPage = resultArticles?.paginationInfo?.lastPage ?? 1;
    emit('filter', resultArticles.collection);
}

defineExpose({performSearch});

async function search() {
    filters.currentPage = (route.query?.page) ? parseInt(route.query?.page, 10) : 1;
    filters.lastPage = 1;

    if (route.name === 'categories' || categoryId.value !== predefinedCategoryId.value) {
        if (!categoryId.value || categoryId.value === ALL) {
            await router.push({
                name: 'search',
                query: {q: filters.query},
            });
        } else {
            const mainCategory = categories.value.find((c) => String(c._id) === String(categoryId.value));
            await router.push({
                name: 'category',
                params: {
                    name: StringTools.simplify(mainCategory.name),
                    id: String(categoryId.value),
                },
            });
        }
    } else {
        await performSearch();
    }
}

/** nur Kinder mit hasArticles (1. Ebene), inkl. deren children (2. Ebene) */
const filteredCategory = computed(() => {
    // find mainCategory by categoryId
    const mainCategory = categories.value.find((c) => String(c._id) === String(categoryId.value));
    if (!mainCategory) return null;
    const filteredChildren = mainCategory.children?.filter((child: any) => child.hasArticles) || [];
    return {
        ...mainCategory,
        children: filteredChildren,
    };
});

onMounted(() => {
    filters.query = route.query?.q ?? '';
    if (filters.query) {
        performSearch();
    }
    if (props.predefinedCategory) {
        categoryId.value = String(props.predefinedCategory._id);
        search();
    }
});

watch(
    () => route.query,
    () => {
        if (route.query?.q) {
            filters.query = route.query.q;
        }
    },
);
</script>
